<template>
  <div class="sales-by-customer">
    <!-- Quick periods -->
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Customer</label>
          <FormDropdown v-model="filters.customer" :options="customerOptions" placeholder="All customers" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Search</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Customer name or phone" />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-users"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Net Sales</div>
          <div class="rp-card-value">{{ formatMoney(summary.net_sales) }}</div>
          <div class="rp-card-sub">
            {{ plural(summary.customer_count, 'customer') }} · avg {{ formatMoney(summary.avg_per_customer) }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-cash-banknote"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Collected</div>
          <div class="rp-card-value">{{ formatMoney(collectedTotal) }}</div>
          <div class="rp-card-sub">{{ formatMoney(summary.collected) }} of it on earlier dues</div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-clock-dollar"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Sold on Due</div>
          <div class="rp-card-value">{{ formatMoney(summary.due_created) }}</div>
          <div class="rp-card-sub">left unpaid on this period's invoices</div>
        </div>
      </div>
      <button type="button" class="rp-card rp-card-action tone-red" :class="{ selected: filters.show === 'due' }"
              :title="filters.show === 'due' ? 'Show all customers' : 'Show customers who owe'" @click="toggleDue">
        <span class="rp-card-icon"><i class="ti ti-alert-circle"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Balance Due</div>
          <div class="rp-card-value" :class="{ 'text-danger': Number(summary.balance) > 0 }">{{ formatMoney(summary.balance) }}</div>
          <div class="rp-card-sub">owed by {{ plural(summary.due_count, 'customer') }} on {{ formatDate(filters.date_to) }}</div>
        </div>
      </button>
    </div>

    <!-- Insights -->
    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Top customers</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: SALES_COLOR }"></i>Net sales</span>
            <span><i class="rp-dot" :style="{ background: PROFIT_COLOR }"></i>Profit</span>
          </span>
        </div>
        <div v-show="summary.top_customers.length" class="rp-chart" :style="{ height: `${chartHeight}px` }">
          <canvas ref="topChart"></canvas>
        </div>
        <p v-if="!summary.top_customers.length" class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Customer mix</div>
        <div class="rp-bars">
          <div v-for="part in mixBars" :key="part.label">
            <div class="d-flex justify-content-between small mb-1">
              <span><i class="ti me-1" :class="part.icon"></i>{{ part.label }}</span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(part.value) }} <span class="text-muted fw-normal">({{ part.share }}%)</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="part.tone" :style="{ width: `${part.share}%` }"></div></div>
          </div>
        </div>
        <div class="rp-note mt-3">
          {{ plural(summary.registered_count, 'registered customer') }} bought ·
          <strong>{{ formatInt(summary.repeat_count) }}</strong> came back more than once ·
          margin <strong>{{ summary.margin }}%</strong>
        </div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Highest balances due</div>
        <ul v-if="summary.top_balances.length" class="rp-ranked">
          <li v-for="(customer, index) in summary.top_balances" :key="customer.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="customer.name">{{ customer.name }}</div>
              <small class="text-muted">
                {{ customer.last_purchase ? `last bought ${formatDate(customer.last_purchase)}` : 'no purchase in period' }}
              </small>
            </div>
            <span class="fw-semibold text-nowrap text-danger">{{ formatMoney(customer.balance) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No customer owes anything.</p>
      </div>
    </div>

    <!-- Customers table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in showPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: (filters.show || 'all') === pill.key }" @click="setShow(pill.key)">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button v-if="row.invoices.length" type="button" class="rp-expand"
                          :title="expanded[row.key] ? 'Hide invoices' : 'Show invoices'" @click="toggleRow(row.key)">
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td>
                  <div class="fw-medium">
                    {{ row.customer }}
                    <span v-if="row.is_guest" class="rp-badge muted ms-1">Walk-in</span>
                  </div>
                  <span v-if="showPhone(row.customer_phone)" class="rp-sub">{{ row.customer_phone }}</span>
                </td>
                <td class="text-center">
                  <span class="rp-count" :class="{ zero: !row.invoice_count }">{{ row.invoice_count }}</span>
                </td>
                <td class="text-end">
                  {{ formatMoney(row.sales) }}
                  <span v-if="Number(row.discount)" class="rp-sub">−{{ formatMoney(row.discount) }} disc.</span>
                </td>
                <td class="text-end" :class="Number(row.returned) ? 'text-danger' : 'text-muted'">
                  {{ Number(row.returned) ? `−${formatMoney(row.returned)}` : '—' }}
                </td>
                <td class="text-end fw-semibold">{{ formatMoney(row.net_sales) }}</td>
                <td class="text-end">
                  <span :class="netClass(row.profit)">{{ formatMoney(row.profit) }}</span>
                  <span v-if="row.invoice_count" class="rp-sub">{{ row.margin }}%</span>
                </td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.due_created) }">
                  {{ Number(row.due_created) ? formatMoney(row.due_created) : '—' }}
                </td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.collected) }">
                  {{ Number(row.collected) ? formatMoney(row.collected) : '—' }}
                </td>
                <td class="text-end" :class="Number(row.balance) > 0 ? 'text-danger fw-semibold' : 'text-muted'">
                  {{ Number(row.balance) ? formatMoney(row.balance) : '—' }}
                </td>
                <td class="text-nowrap">{{ row.last_purchase ? formatDate(row.last_purchase) : '—' }}</td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title"><i class="ti ti-file-invoice me-1"></i>{{ row.customer }} — invoices in the period</div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Invoice</th>
                            <th class="text-end">Items</th>
                            <th class="text-end">Total</th>
                            <th class="text-end">Paid</th>
                            <th class="text-end">Due</th>
                            <th class="text-end">Returned</th>
                            <th>Payment</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="invoice in row.invoices" :key="invoice.invoice_no">
                            <td class="text-nowrap">{{ formatDate(invoice.date) }} <small class="text-muted">{{ invoice.time }}</small></td>
                            <td class="fw-semibold">{{ invoice.invoice_no }}</td>
                            <td class="text-end">{{ invoice.item_count }}</td>
                            <td class="text-end fw-semibold">{{ formatMoney(invoice.total) }}</td>
                            <td class="text-end">{{ formatMoney(invoice.paid) }}</td>
                            <td class="text-end" :class="{ 'text-danger': Number(invoice.due) }">
                              {{ Number(invoice.due) ? formatMoney(invoice.due) : '—' }}
                            </td>
                            <td class="text-end">{{ Number(invoice.returned_amount) ? formatMoney(invoice.returned_amount) : '—' }}</td>
                            <td>{{ invoice.payment_method }}</td>
                            <td><span class="rp-badge" :class="STATUS_TONES[invoice.payment_status]">{{ invoice.payment_label }}</span></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-users"></i>
                  <div class="fw-semibold">No customers found</div>
                  <small class="text-muted">Try another period or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th class="text-end">Total ({{ plural(rows.length, 'customer') }})</th>
              <th class="text-center">{{ formatInt(summary.invoice_count) }}</th>
              <th class="text-end">{{ formatMoney(summary.sales) }}</th>
              <th class="text-end text-danger">{{ Number(summary.returned) ? `−${formatMoney(summary.returned)}` : '—' }}</th>
              <th class="text-end">{{ formatMoney(summary.net_sales) }}</th>
              <th class="text-end" :class="netClass(summary.profit)">{{ formatMoney(summary.profit) }}</th>
              <th class="text-end">{{ formatMoney(summary.due_created) }}</th>
              <th class="text-end">{{ formatMoney(summary.collected) }}</th>
              <th class="text-end text-danger">{{ formatMoney(summary.balance) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Sales are as invoiced; net sales = sales − returns of the period. Collected counts due receipts in this store.
      Balance due is what the customer owed on the last day of the period, across all stores.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { PROFIT_COLOR, SALES_COLOR } from './salesReportMixin';

const EMPTY_SUMMARY = {
  qty: 0, sales: 0, discount: 0, vat: 0, returned: 0, net_sales: 0, profit: 0, paid: 0, due_created: 0,
  collected: 0, balance: 0, invoice_count: 0, customer_count: 0, registered_count: 0, repeat_count: 0,
  guest_sales: 0, registered_sales: 0, guest_share: 0, due_count: 0, margin: 0, avg_per_customer: 0,
  top_customers: [], top_balances: [], show_counts: { all: 0, sales: 0, due: 0 },
};
const STATUS_TONES = { paid: 'success', partial: 'warning', unpaid: 'danger' };

export default {
  name: 'SalesByCustomerReport',
  mixins: [inventoryReport, periodReport, salesReport],

  reportUrl: apiUrls.sales_by_customer_report.get,
  fileName: 'sales_by_customer',
  columns: [
    { key: 'customer', label: 'Customer' },
    { key: 'invoice_count', label: 'Invoices', class: 'text-center', numeric: true },
    { key: 'sales', label: 'Sales', class: 'text-end', numeric: true },
    { key: 'returned', label: 'Returned', class: 'text-end', numeric: true },
    { key: 'net_sales', label: 'Net Sales', class: 'text-end', numeric: true },
    { key: 'profit', label: 'Profit', class: 'text-end', numeric: true },
    { key: 'due_created', label: 'Sold on Due', class: 'text-end', numeric: true },
    { key: 'collected', label: 'Collected', class: 'text-end', numeric: true },
    { key: 'balance', label: 'Balance Due', class: 'text-end', numeric: true },
    { key: 'last_purchase', label: 'Last Purchase' },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), customer: null, show: null, warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      SALES_COLOR,
      PROFIT_COLOR,
      STATUS_TONES,
    };
  },

  computed: {
    hasActiveFilters() {
      const { customer, show, search } = this.filters;
      return this.periodChanged || !!(customer || show || search.trim());
    },

    collectedTotal() {
      return (Number(this.summary.paid) || 0) + (Number(this.summary.collected) || 0);
    },

    showPills() {
      const counts = this.summary.show_counts;
      return [
        { key: 'all', label: 'All customers', count: counts.all },
        { key: 'sales', label: 'With sales', count: counts.sales },
        { key: 'due', label: 'With balance due', count: counts.due },
      ];
    },

    mixBars() {
      const s = this.summary;
      const total = Number(s.sales) || 0;
      const share = (v) => (total ? Math.round(((Number(v) || 0) / total) * 100) : 0);
      return [
        { label: 'Registered customers', icon: 'ti-user-check', tone: 'info', value: s.registered_sales, share: share(s.registered_sales) },
        { label: 'Walk-in (guest)', icon: 'ti-walk', tone: 'warning', value: s.guest_sales, share: share(s.guest_sales) },
      ];
    },

    chartHeight() {
      return Math.max(170, this.summary.top_customers.length * 26 + 30);
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  watch: {
    'filters.show'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      return { ...this.periodParams(), ...this.customerParams(), ...(this.filters.show ? { show: this.filters.show } : {}) };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), customer: null, show: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      const top = this.summary.top_customers;
      this.$nextTick(() => this.drawBars('top', this.$refs.topChart, top.map((c) => c.name), [
        { label: 'Net sales', data: top.map((c) => c.net_sales), color: SALES_COLOR },
        { label: 'Profit', data: top.map((c) => c.profit), color: PROFIT_COLOR },
      ], { horizontal: true }));
    },

    setShow(key) {
      this.filters.show = key === 'all' ? null : key;
    },

    toggleDue() {
      this.filters.show = this.filters.show === 'due' ? null : 'due';
    },

    showPhone(phone) {
      return phone && !/^0+$/.test(phone);
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${escapeHtml(r.customer)}${this.showPhone(r.customer_phone) ? `<br><small>${escapeHtml(r.customer_phone)}</small>` : ''}</td>
          <td class="num">${r.invoice_count}</td>
          <td class="num">${this.formatMoney(r.sales)}</td>
          <td class="num">${this.formatMoney(r.returned)}</td>
          <td class="num">${this.formatMoney(r.net_sales)}</td>
          <td class="num">${this.formatMoney(r.profit)}</td>
          <td class="num">${this.formatMoney(r.due_created)}</td>
          <td class="num">${this.formatMoney(r.collected)}</td>
          <td class="num">${this.formatMoney(r.balance)}</td>
          <td>${r.last_purchase ? this.formatDate(r.last_purchase) : '—'}</td>
        </tr>`).join('');
      const table = `<table><thead><tr><th>Customer</th><th class="num">Invoices</th><th class="num">Sales</th>
          <th class="num">Returned</th><th class="num">Net Sales</th><th class="num">Profit</th><th class="num">Sold on Due</th>
          <th class="num">Collected</th><th class="num">Balance Due</th><th>Last Purchase</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td class="num">Total</td><td class="num">${s.invoice_count}</td>
          <td class="num">${this.formatMoney(s.sales)}</td><td class="num">${this.formatMoney(s.returned)}</td>
          <td class="num">${this.formatMoney(s.net_sales)}</td><td class="num">${this.formatMoney(s.profit)}</td>
          <td class="num">${this.formatMoney(s.due_created)}</td><td class="num">${this.formatMoney(s.collected)}</td>
          <td class="num">${this.formatMoney(s.balance)}</td><td></td></tr></tfoot></table>`;
      const show = this.showPills.find((p) => p.key === (this.filters.show || 'all'))?.label;
      const search = this.filters.search.trim();
      this.openPrint(
        'Sales by Customer',
        [
          [this.periodText(), `Show: ${show}`, this.customerText(), this.storeText(),
            ...(search ? [`Search: ${search}`] : [])].join(' · '),
          `Printed ${new Date().toLocaleString()}`,
        ],
        [
          ['Net Sales', `${this.formatMoney(s.net_sales)} · ${this.plural(s.customer_count, 'customer')}`],
          ['Collected', this.formatMoney(this.collectedTotal)],
          ['Sold on Due', this.formatMoney(s.due_created)],
          ['Balance Due', `${this.formatMoney(s.balance)} · ${this.plural(s.due_count, 'customer')}`],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
